import { formatComposerUploadProgress } from '@/lib/uploads/composer-upload-progress';

/**
 * What the waiting first message of a thread shows while the files it was
 * sent with are still uploading (#4844). Pure: the server's view of the
 * send, the uploads this device is running, live byte counts and the clock
 * go in; one view model comes out.
 */

export type PendingUploadSendServerFile = {
  uploadId: string;
  filename: string;
  sizeBytes: number;
  uploadedBytes: number;
  status: 'uploading' | 'ready' | 'failed' | 'removed';
  failureReason?: string;
};

/** A file the message carried already uploaded at the press. */
export type PendingUploadSendPresentFile = {
  filename: string;
  sizeBytes: number;
};

export type PendingUploadSendServerView = {
  pendingSendId: string;
  isOwnSend: boolean;
  status: 'pending_uploads' | 'starting' | 'started' | 'failed' | 'cancelled';
  prompt: string;
  lastProgressAt: number;
  files: PendingUploadSendServerFile[];
  presentFiles?: PendingUploadSendPresentFile[];
};

/** A file this device is uploading (from useBackgroundUploadJobs). */
export type PendingUploadLocalJob = {
  uploadId: string;
  pendingSendId: string;
  state: 'uploading' | 'failed';
  failure?: string;
  /** Stored here; only the server's record is still owed. */
  saving?: boolean;
};

export type PendingUploadFailureKind =
  | 'network'
  | 'server'
  | 'too_large'
  | 'file_unavailable'
  | 'unknown';

export type PendingUploadFileView = {
  uploadId: string;
  filename: string;
  sizeBytes: number;
  loadedBytes: number;
  /** `waiting`: uploading on another device that has gone quiet. */
  state: 'uploading' | 'ready' | 'failed' | 'waiting';
  /**
   * Uploading, with every byte sent: the server is verifying and saving
   * it, so the row says so instead of holding at 100%.
   */
  finishing: boolean;
  failure: PendingUploadFailureKind | null;
  canRetry: boolean;
  canRemove: boolean;
  /** The manager owns a local job; otherwise remove through the server. */
  removeVia: 'manager' | 'server';
};

export type PendingUploadSendStatus =
  | 'uploading'
  /** Every file still in flight has all its bytes sent; saving them. */
  | 'finishing'
  | 'needs_attention'
  | 'waiting_other_device'
  | 'starting'
  | 'start_failed';

export type PendingUploadSendViewState = {
  pendingSendId: string;
  prompt: string;
  status: PendingUploadSendStatus;
  /** Files still part of the message (removed ones are left out). */
  fileCount: number;
  uploadingCount: number;
  failedCount: number;
  loadedBytes: number;
  totalBytes: number;
  canRetryStart: boolean;
  files: PendingUploadFileView[];
};

/**
 * The message footer's percent counts only the files still on their way, so
 * with one file uploading it reads exactly as that file's row does (both
 * floor the same bytes) instead of a point higher for the files already in.
 */
export function pendingUploadInFlightBytes(
  files: ReadonlyArray<
    Pick<PendingUploadFileView, 'state' | 'loadedBytes' | 'sizeBytes'>
  >
): { loadedBytes: number; totalBytes: number } {
  const inFlight = files.filter((file) => file.state === 'uploading');
  return {
    loadedBytes: inFlight.reduce((sum, file) => sum + file.loadedBytes, 0),
    totalBytes: inFlight.reduce((sum, file) => sum + file.sizeBytes, 0),
  };
}

/** "Uploading 1 file…" reads "Uploading 1 file · 4%" when a percent follows. */
export function withoutTrailingEllipsis(text: string): string {
  return text.replace(/\s*(?:…|\.\.\.)$/u, '');
}

/** Quiet for this long with no uploader here: the sending device went away. */
export const PENDING_UPLOAD_REMOTE_STALE_MS = 30_000;

const FAILURE_KINDS: ReadonlySet<string> = new Set([
  'network',
  'server',
  'too_large',
  'file_unavailable',
]);

export function pendingUploadFailureKind(
  reason: string | undefined
): PendingUploadFailureKind {
  return reason && FAILURE_KINDS.has(reason)
    ? (reason as PendingUploadFailureKind)
    : 'unknown';
}

/** Retry can't help a file that is too big or whose bytes are gone. */
function failureIsRetryable(kind: PendingUploadFailureKind) {
  return kind !== 'too_large' && kind !== 'file_unavailable';
}

function clampBytes(value: number, max: number) {
  return Math.min(Math.max(value, 0), Math.max(max, 0));
}

/** Present files have no upload id; their rows still need a stable key. */
export const PENDING_UPLOAD_PRESENT_FILE_PREFIX = 'present:';

function presentFileView(
  file: PendingUploadSendPresentFile,
  index: number
): PendingUploadFileView {
  const sizeBytes = Math.max(file.sizeBytes, 0);
  return {
    uploadId: `${PENDING_UPLOAD_PRESENT_FILE_PREFIX}${index}`,
    filename: file.filename,
    sizeBytes,
    loadedBytes: sizeBytes,
    state: 'ready',
    finishing: false,
    failure: null,
    canRetry: false,
    canRemove: false,
    removeVia: 'server',
  };
}

export function getPendingUploadSendViewState(input: {
  server: PendingUploadSendServerView | null | undefined;
  localJobs: readonly PendingUploadLocalJob[];
  /** Live bytes sent by this device, by upload id. */
  localLoadedBytes?: Readonly<Record<string, number | undefined>>;
  now: number;
}): PendingUploadSendViewState | null {
  const { server, localJobs, now } = input;
  if (!server || server.status === 'cancelled') return null;
  // A job here means this device holds the bytes, even before its first
  // progress report. One still binding to its send has no send id yet.
  const jobs = new Map(
    localJobs
      .filter(
        (job) =>
          job.pendingSendId === server.pendingSendId || job.pendingSendId === ''
      )
      .map((job) => [job.uploadId, job])
  );
  const owner = server.isOwnSend;
  const active = server.files.filter((file) => file.status !== 'removed');
  const stillUploading = active.filter(
    (file) =>
      file.status === 'uploading' && jobs.get(file.uploadId)?.state !== 'failed'
  );
  // The sender is looking at it, nothing is uploading here, and the server
  // has heard nothing for a while: the bytes are on another device.
  const waitingOnOtherDevice =
    owner &&
    server.status === 'pending_uploads' &&
    stillUploading.length > 0 &&
    stillUploading.every((file) => !jobs.has(file.uploadId)) &&
    now - server.lastProgressAt > PENDING_UPLOAD_REMOTE_STALE_MS;

  const files = active.map((file): PendingUploadFileView => {
    const job = jobs.get(file.uploadId);
    const removeVia = job ? 'manager' : 'server';
    const base = {
      uploadId: file.uploadId,
      filename: file.filename,
      sizeBytes: file.sizeBytes,
      removeVia,
    } as const;
    if (file.status === 'ready')
      return {
        ...base,
        loadedBytes: file.sizeBytes,
        state: 'ready',
        finishing: false,
        failure: null,
        canRetry: false,
        canRemove: false,
      };
    // This device's job is the live truth for its file: a Retry sets it
    // uploading again before the server hears any progress.
    if (job ? job.state === 'failed' : file.status === 'failed') {
      const failure = pendingUploadFailureKind(
        job?.failure ?? file.failureReason
      );
      return {
        ...base,
        loadedBytes: clampBytes(file.uploadedBytes, file.sizeBytes),
        state: 'failed',
        finishing: false,
        failure,
        // Only the device holding the bytes can send them again.
        canRetry: owner && Boolean(job) && failureIsRetryable(failure),
        canRemove: owner,
      };
    }
    const live = job ? input.localLoadedBytes?.[file.uploadId] : undefined;
    const waiting = waitingOnOtherDevice && !job;
    const loadedBytes = job?.saving
      ? Math.max(file.sizeBytes, 0)
      : clampBytes(live ?? file.uploadedBytes, file.sizeBytes);
    return {
      ...base,
      loadedBytes,
      state: waiting ? 'waiting' : 'uploading',
      // An empty file has nothing to send: it is saving from the start.
      finishing: !waiting && loadedBytes >= file.sizeBytes,
      failure: null,
      canRetry: false,
      canRemove: owner && waiting,
    };
  });

  // Files that were already in at the press come first, as ready rows.
  files.unshift(...(server.presentFiles ?? []).map(presentFileView));
  const failedCount = files.filter((file) => file.state === 'failed').length;
  const inFlight = files.filter(
    (file) => file.state === 'uploading' || file.state === 'waiting'
  );
  const uploadingCount = inFlight.length;
  const allSent =
    inFlight.length > 0 &&
    inFlight.every((file) => file.state === 'uploading' && file.finishing);
  const status: PendingUploadSendStatus =
    server.status === 'starting' || server.status === 'started'
      ? 'starting'
      : server.status === 'failed'
        ? 'start_failed'
        : failedCount > 0
          ? 'needs_attention'
          : waitingOnOtherDevice
            ? 'waiting_other_device'
            : allSent
              ? 'finishing'
              : 'uploading';
  return {
    pendingSendId: server.pendingSendId,
    prompt: server.prompt,
    status,
    fileCount: files.length,
    uploadingCount,
    failedCount,
    loadedBytes: files.reduce((sum, file) => sum + file.loadedBytes, 0),
    totalBytes: files.reduce((sum, file) => sum + file.sizeBytes, 0),
    canRetryStart: owner && server.status === 'failed',
    files: files.map((file) =>
      status === 'start_failed' || status === 'starting'
        ? { ...file, canRetry: false, canRemove: false }
        : file
    ),
  };
}

const KB = 1000;
const MB = 1000 * 1000;
/** Below this, sizes read in kB: "0.0 MB" says nothing. */
const KB_BELOW_BYTES = 0.1 * MB;

function formatUnit(
  value: number,
  unit: 'kilobyte' | 'megabyte',
  digits: number,
  locale?: string
) {
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/**
 * "383 MB", "4.2 MB" (whole MB from 10 MB up, as the composer chip does),
 * and whole kB under 0.1 MB ("48 kB"), never "0.0 MB".
 */
export function formatPendingUploadSize(bytes: number, locale?: string) {
  const size = Math.max(bytes, 0);
  if (size < KB_BELOW_BYTES)
    return formatUnit(Math.round(size / KB), 'kilobyte', 0, locale);
  const mb = size / MB;
  return formatUnit(mb, 'megabyte', mb >= 10 ? 0 : 1, locale);
}

/**
 * The bytes half of a file row's progress: "1.2 / 4.2 MB" as the composer
 * chip says it, or "12 / 48 kB" for a file under 0.1 MB.
 */
export function formatPendingUploadBytes(
  loadedBytes: number,
  totalBytes: number,
  locale?: string
) {
  const total = Math.max(totalBytes, 0);
  const loaded = Math.min(Math.max(loadedBytes, 0), total);
  if (total < KB_BELOW_BYTES)
    return `${new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(
      Math.floor(loaded / KB)
    )} / ${formatPendingUploadSize(total, locale)}`;
  return formatComposerUploadProgress(
    { loadedBytes: loaded, totalBytes: total },
    locale
  ).bytes;
}
